import React, { useState, useRef, useEffect } from "react";
import { getAllClassNames } from "helpers";
import styles from "./DropdownCheckboxes.module.scss";
import Text from "components/text/Text";
import { Arrow, Close } from "assets/icons";
import { useClickOutside } from "hooks";
import strings from "assets/strings";
import { SERACH_DOCTORS_API } from "assets/constants";
import { settings } from "assets/settings";

export type ItemProps = {
  value: string;
  label: string;
  isSelected: boolean;
};

type IDropdownProps = {
  title?: string;
  className?: string;
  dropDownClass?: string;
  objectType: string;
  attribute: string;
  keyToNormalize: string;
  labelToNormalize: string;
  disabled?: boolean;
  isSwitchSelected?: boolean;
  placeholder?: string;
  setQueryParams: (selectedMedicalServices: string[]) => void;
}

export const DropdownCheckboxes = ({
  title,
  className,
  dropDownClass,
  objectType,
  attribute,
  keyToNormalize,
  labelToNormalize,
  disabled,
  isSwitchSelected,
  placeholder,
  setQueryParams
}: IDropdownProps) => {

  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<string[]>([]);
  const [selectCheckBoxes, setSelectCheckBoxes] = useState<ItemProps[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropdownRef, () => setShowDropdown(false));

  const isSelected = (value: string): boolean => {
    return selectedCheckboxes.includes(value);
  }

  const handleArrowKeyDown = (event: React.KeyboardEvent<SVGSVGElement>) => {
    if (event.key === "Enter") {
      setShowDropdown((prevState) => !prevState);
    }
  };

  const handleCloseKeyDown = (event: React.KeyboardEvent<SVGSVGElement>, item: string) => {
    if (event.key === "Enter") {
      handleSelectedOption(item);
    }
  };

  useEffect(() => {
    if (selectedCheckboxes.length > 0) {
     setSelectCheckBoxes([]);
    }
  }, [])
  
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showDropdown) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDropdown]);

  useEffect(() => {
    const fetchOptions = async () => {
      const response = await fetch(`https://${settings.searchApiUrl}${SERACH_DOCTORS_API}/${attribute}?ObjectType=${objectType}`);
      const options: any[] = await response.json();
      const normalizedOptions = options.map(item => {
        return {
          value: item[keyToNormalize],
          label: item[labelToNormalize],
          isSelected: false
        };
      });

      setSelectCheckBoxes(normalizedOptions);     
    }
    fetchOptions();
  }, [])

  useEffect(() => {

    if (isSwitchSelected) {
      setSelectedCheckboxes([]);
    }
  }, [isSwitchSelected])

  const handleSelectedOption = (value: string): void => {
    let temp = [...selectedCheckboxes];
    if (value !== strings.searchDoctors.allSubMedicalFields) {
      temp = temp.filter((item) => item !== strings.searchDoctors.allSubMedicalFields);
    }
    if (value === strings.searchDoctors.allSubMedicalFields) {
      temp = temp.filter((item) => item === strings.searchDoctors.allSubMedicalFields);
    }
    const index = temp.findIndex((val) => val === value);
    if (index !== -1) temp.splice(index, 1);
    else temp.push(value);
    setSelectedCheckboxes(temp);
    setQueryParams(temp);
    setSelectedCheckboxes(temp);
  };

  return (
    <div ref={dropdownRef}  className={getAllClassNames([styles.container, className])}>
      <div className={styles.divTop}>
        {title && <Text className={styles.title} text={title} />}
      </div>
      {!disabled ? <div
        //onClick={() => setShowDropdown(true)}
        className={getAllClassNames([
          styles.dropdownContainer,
        ])}
        onClick={() => { setShowDropdown((prevState) => !prevState)}}
      >
        {!isSwitchSelected && selectedCheckboxes.map((item) => (
          <div
            key={item}
            className={getAllClassNames([
              styles.bubble,
              "ais-RefinementList-item",
            ])}
          >
            <span
              className={getAllClassNames([
                "ais-RefinementList-labelText",
                styles.label,
              ])}
            >
              {item}
            </span>
            <Close
              onClick={() => {
                handleSelectedOption(item);
              }}
              className={styles.icon}
              role="button" // Added role for screen readers
              tabIndex={0} // Make the element focusable
              onKeyDown={(event) => handleCloseKeyDown(event, item)}
              aria-label={`${strings.globalStrings.remove} ${item}`} 
            />
          </div>
        ))}
        <span className={styles.placeholder}>{selectedCheckboxes.length === 0 ? placeholder : ""}</span>
        <Arrow
          className={styles.iconArrow}
          onClick={() => {handleArrowKeyDown}}
          aria-label={showDropdown ? strings.globalStrings.collapse : strings.globalStrings.expand} // Added aria-label for accessibility
          role="button" // Added role for screen readers
          tabIndex={0} // Make the element focusable     
          onKeyDown={handleArrowKeyDown}    
        />
      </div> : 
      <div className={getAllClassNames([styles.dropdownContainer,styles.disabled])}> 
      <Arrow
          className={styles.iconArrow}
          onClick={() => {handleArrowKeyDown}}
          aria-label={showDropdown ? strings.globalStrings.collapse : strings.globalStrings.expand} // Added aria-label for accessibility
          role="button" // Added role for screen readers
          tabIndex={0} // Make the element focusable        
          onKeyDown={handleArrowKeyDown}  
        />
      </div>}
      {showDropdown && (
        <div className={getAllClassNames([styles.dropdown, dropDownClass])}>
          <ul>
          {selectCheckBoxes.map((item) => (
            <li
              key={item.value}
              className={getAllClassNames([
                "ais-RefinementList-item",
                isSelected(item.label) && "ais-RefinementList-item--selected",
              ])}
            >
              <label>
                <input
                  type="checkbox"
                  value={item.value}
                  checked={isSelected(item.label)}
                  className={styles.checkbox}
                  onChange={() => {
                    handleSelectedOption(item.label);
                  }}
                />
                {item.label}
              </label>
            </li>
          ))}
          </ul>
          <div className={styles.height} />
        </div>
      )}
    </div>
  );
};
